import zoomLogo from '@src/assets/platform-logos/zoom-logo.png';
import { ConfirmModal } from '@src/components/common/ConfirmModal';
import {
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Input from '@src/components/common/Input';
import ToggleGroup from '@src/components/common/ToggleGroup';
import VideoDropBox from '@src/components/common/VideoDropBox';
import useVideoCreatorDataInitialization from '@src/hooks/useVideoCreatorDataInitialization';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CircleArrowRightIcon from '@src/svgs/CircleArrowRightIcon';
import InfoIcon from '@src/svgs/InfoIcon';
import LinkIcon from '@src/svgs/LinkIcon';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useState } from 'react';
import styled from 'styled-components';

import MultiFileUploadConcat from '../../common/MultiFileUploadConcat';
import {
  acceptedFilesLine,
  MULTI_SUBTITLE,
  stepSubtitle,
  UPLOAD_STARTS_NOTE,
  UPLOAD_STEP_TITLE,
  URL_INVALID,
  URL_PLACEHOLDER,
  ZOOM_STEP_TITLE,
} from './uploadCopy';
import { DialogNote as Note } from './uploadDialogStyles';
import { useZoomImport } from './useZoomImport';
import ZoomAuthButton from './ZoomAuthButton';
import ZoomRecordingsList from './ZoomRecordingsList';

import type { ZoomRecordingData } from './useStoryCreator';

const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

type Props = {
  handleStoryVideoUpload: (f: File[]) => void;
  handleStoryUrlUpload?: (url: string) => void;
  setStoryName?: (s: string) => void;
  setZoomRecordingData?: (data: ZoomRecordingData | null) => void;
  maxUploadSizeGB: number;
};

/**
 * Step 1 of the Add Stories dialog (PLAT-11941): one place to drop or browse a
 * video, paste a link, or import from Zoom. Picking a file starts the upload
 * and moves on to Story Details — there is nothing to confirm here, so the
 * footer only appears for the two sources that need one (a link, a recording).
 */
export function UploadStep({
  handleStoryVideoUpload,
  handleStoryUrlUpload,
  setStoryName,
  setZoomRecordingData,
  maxUploadSizeGB,
}: Props) {
  useVideoCreatorDataInitialization();

  const userIdentity = useUserIdentity();
  const videoCreator = useVideoCreatorStore();
  const {
    enableMultiFileConcat,
    enableUrlUpload,
    enableZoomConnection,
    storyManagerAddStoriesEnableButtons,
  } = useFlagsCombination(userIdentity.currentRole);

  const [uploadMode, setUploadMode] = useState<'single' | 'multi'>('single');
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');

  const zoom = useZoomImport({
    setStoryName,
    setZoomRecordingData,
    handleStoryUrlUpload,
  });

  const submitUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!isValidUrl(url)) {
      setUrlError(URL_INVALID);
      return;
    }
    handleStoryUrlUpload?.(url);
  };

  if (zoom.showZoomRecordings && enableZoomConnection) {
    return (
      <>
        <DialogHeader>
          <TitleWithLogo>
            {ZOOM_STEP_TITLE}
            <img src={zoomLogo} alt="" width={56} />
          </TitleWithLogo>
          <DialogSubtitle>{stepSubtitle(1)}</DialogSubtitle>
        </DialogHeader>
        <ZoomRecordingsList
          onSelectRecording={zoom.selectRecording}
          refreshTrigger={zoom.showZoomRecordings}
          zoomPersonId={zoom.zoomPersonId || undefined}
        />
        <DialogFooter>
          <SecondaryButton type="button" onClick={zoom.closeRecordings}>
            Back
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={!zoom.selectedZoomRecording}
            onClick={zoom.importSelectedRecording}
          >
            Import Recording
          </PrimaryButton>
        </DialogFooter>
      </>
    );
  }

  const canImportUrl = enableUrlUpload && !!urlInput.trim();

  return (
    <>
      <DialogHeader>
        <DialogTitle>{UPLOAD_STEP_TITLE}</DialogTitle>
        <DialogSubtitle>
          {uploadMode === 'multi'
            ? `${stepSubtitle(1)} · ${MULTI_SUBTITLE}`
            : stepSubtitle(1)}
        </DialogSubtitle>
      </DialogHeader>

      {storyManagerAddStoriesEnableButtons && enableMultiFileConcat && (
        <ToggleGroup
          ariaLabel="Upload mode"
          value={uploadMode}
          onChange={setUploadMode}
          options={[
            { id: 'single', label: 'Single Video' },
            { id: 'multi', label: 'Multiple Videos' },
          ]}
        />
      )}

      {storyManagerAddStoriesEnableButtons &&
        (uploadMode === 'single' ? (
          <VideoDropBox
            onFiles={handleStoryVideoUpload}
            showToast={toast => videoCreator.showToast(toast)}
            hint={acceptedFilesLine(maxUploadSizeGB)}
            inputTestId="story-video-input"
          />
        ) : (
          <MultiFileUploadConcat
            onFilesChange={handleStoryVideoUpload}
            maxUploadSizeGB={maxUploadSizeGB}
          />
        ))}

      {storyManagerAddStoriesEnableButtons && enableUrlUpload && (
        <>
          <OrDivider>or</OrDivider>
          <Input
            fullWidth
            type="url"
            aria-label="Video link"
            placeholder={URL_PLACEHOLDER}
            value={urlInput}
            error={urlError || undefined}
            icon={<LinkIcon strokeColor="currentColor" />}
            onChange={e => {
              setUrlInput(e.target.value);
              setUrlError('');
            }}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Enter') submitUrl();
            }}
            trailing={
              <SubmitLinkButton
                type="button"
                aria-label="Import link"
                disabled={!urlInput.trim()}
                onClick={submitUrl}
              >
                <CircleArrowRightIcon
                  fillColor={
                    urlInput.trim() ? 'var(--primary)' : 'var(--text-muted)'
                  }
                />
              </SubmitLinkButton>
            }
          />
          {enableZoomConnection &&
            (!zoom.isZoomAuthenticated ? (
              <ZoomAuthButton onAuthSuccess={zoom.handleAuthSuccess} />
            ) : (
              <ZoomRow>
                <ZoomRecordingsButton
                  type="button"
                  onClick={zoom.openRecordings}
                >
                  View Recordings
                  <img src={zoomLogo} alt="Zoom" width={56} />
                </ZoomRecordingsButton>
                <SecondaryButton
                  type="button"
                  onClick={() => zoom.setShowDisconnectModal(true)}
                >
                  Disconnect
                </SecondaryButton>
              </ZoomRow>
            ))}
        </>
      )}

      {storyManagerAddStoriesEnableButtons && (
        <Note>
          <InfoIcon />
          <span>{UPLOAD_STARTS_NOTE}</span>
        </Note>
      )}

      {canImportUrl && (
        <DialogFooter>
          <PrimaryButton type="button" onClick={submitUrl}>
            Import
          </PrimaryButton>
        </DialogFooter>
      )}

      {zoom.showDisconnectModal && (
        <ConfirmModal
          title="Disconnect Zoom?"
          message="You'll need to connect again to import recordings."
          cancelText="Cancel"
          confirmText="Disconnect"
          closeModal={() => zoom.setShowDisconnectModal(false)}
          onCancel={() => zoom.setShowDisconnectModal(false)}
          onConfirm={zoom.disconnect}
        />
      )}
    </>
  );
}

const TitleWithLogo = styled(DialogTitle)`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

// The one structural rule in this dialog: the hairline either side of "or".
const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border-subtle);
  }
`;

const SubmitLinkButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const ZoomRow = styled.div`
  display: flex;
  gap: var(--spacing-sm);
`;

const ZoomRecordingsButton = styled(SecondaryButton)`
  flex: 1;

  img {
    height: 14px;
    width: auto;
  }
`;
